import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API } from '../config';
import { CualiaPreferences } from './CualiaAdvice';

const EconomyProfileComponent = () => {
  const [profile, setProfile] = useState(null);
  const [credit, setCredit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingsAmount, setSavingsAmount] = useState('');
  const [savingsAction, setSavingsAction] = useState(null);
  const [commissions, setCommissions] = useState(null);
  const [ccClassification, setCcClassification] = useState(null);

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      const [profRes, credRes, commRes, ccRes] = await Promise.all([
        axios.get(`${API}/economy/profile`),
        axios.get(`${API}/economy/credit`),
        axios.get(`${API}/economy/commissions`),
        axios.get(`${API}/economy/cc-classification`)
      ]);
      setProfile(profRes.data);
      setCredit(credRes.data);
      setCommissions(commRes.data);
      setCcClassification(ccRes.data);
    } catch (err) {
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSavingsMove = async (direction) => {
    const amt = parseFloat(savingsAmount);
    if (!amt || amt <= 0) return;
    try {
      const url = direction === 'deposit'
        ? `${API}/economy/savings/deposit`
        : `${API}/economy/savings/withdraw`;
      await axios.post(url, { amount: amt });
      setSavingsAmount('');
      setSavingsAction(null);
      fetchProfile();
    } catch (err) {
      alert(err.response?.data?.detail || 'Error al mover fondos');
    }
  };

  if (loading) return <div className="text-center py-12 text-gray-500">Cargando perfil economico...</div>;
  if (!profile) return <div className="text-center py-12 text-gray-500">No hay datos disponibles</div>;

  const totalBalance = (profile.balance_active || 0) + (profile.balance_savings || 0) + (profile.balance_blocked || 0);

  return (
    <div className="space-y-6" data-testid="economy-profile">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900">Mi Economia</h2>
        <p className="text-gray-600">Tu perfil economico dentro de CUALAPP</p>
      </div>

      {/* Balance Zones */}
      <div className="bg-gradient-to-r from-slate-800 to-slate-900 rounded-xl p-6 text-white">
        <h3 className="text-lg font-bold mb-4">Zonas de Balance</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white/10 rounded-lg p-4" data-testid="balance-active">
            <p className="text-xs text-blue-300 mb-1">COIN-F (Activo)</p>
            <p className="text-2xl font-bold">{(profile.balance_active || 0).toLocaleString()}</p>
            <p className="text-xs text-slate-400 mt-1">Circula y genera valor</p>
          </div>
          <div className="bg-white/10 rounded-lg p-4" data-testid="balance-savings">
            <p className="text-xs text-emerald-300 mb-1">COIN-R (Ahorro)</p>
            <p className="text-2xl font-bold">{(profile.balance_savings || 0).toLocaleString()}</p>
            <p className="text-xs text-slate-400 mt-1">Protegido (peso: 60%)</p>
          </div>
          <div className="bg-white/10 rounded-lg p-4" data-testid="balance-blocked">
            <p className="text-xs text-orange-300 mb-1">Bloqueado (Escrow)</p>
            <p className="text-2xl font-bold">{(profile.balance_blocked || 0).toLocaleString()}</p>
            <p className="text-xs text-slate-400 mt-1">Congelado temporalmente</p>
          </div>
        </div>
        <div className="mt-3 text-right text-sm text-slate-300">
          Total: <span className="font-bold text-white">{totalBalance.toLocaleString()} CUALCOIN</span>
        </div>
      </div>

      {/* Savings Actions */}
      <div className="bg-white rounded-xl shadow-md p-6">
        <h3 className="font-semibold text-gray-900 mb-3">Gestionar Ahorro</h3>
        <p className="text-sm text-gray-500 mb-4">Mueve CUALCOIN entre tu zona activa y tu zona de ahorro. El ahorro esta protegido de depreciacion (limite: 60% del total).</p>
        <div className="flex flex-wrap gap-3 items-end">
          <div className="flex-1 min-w-[140px]">
            <label className="block text-sm font-medium text-gray-700 mb-1">Cantidad</label>
            <input
              type="number"
              value={savingsAmount}
              onChange={(e) => setSavingsAmount(e.target.value)}
              placeholder="0.00"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              data-testid="savings-amount-input"
            />
          </div>
          <button
            onClick={() => handleSavingsMove('deposit')}
            className="px-4 py-2 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 text-sm font-medium"
            data-testid="savings-deposit-btn"
          >
            Activo → Ahorro
          </button>
          <button
            onClick={() => handleSavingsMove('withdraw')}
            className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 text-sm font-medium"
            data-testid="savings-withdraw-btn"
          >
            Ahorro → Activo
          </button>
        </div>
      </div>

      {/* Economic Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl shadow-md p-6">
          <h3 className="font-semibold text-gray-900 mb-4">Peso Economico</h3>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Valor Efectivo</span>
              <span className="text-sm font-bold" data-testid="effective-value">{(profile.effective_value || 0).toLocaleString()}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Peso (w)</span>
              <span className="text-sm font-bold">{profile.economic_weight || 1}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Depreciacion</span>
              <span className={`text-sm font-bold ${profile.depreciation_factor < 0.8 ? 'text-red-600' : 'text-emerald-600'}`}>
                {((profile.depreciation_factor || 1) * 100).toFixed(1)}%
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Reputacion</span>
              <span className="text-sm font-bold">{((profile.economic_reputation || 0.5) * 100).toFixed(0)}%</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Riesgo</span>
              <span className={`text-sm font-bold ${(profile.risk_score || 0) > 0.3 ? 'text-red-600' : 'text-emerald-600'}`}>
                {((profile.risk_score || 0) * 100).toFixed(0)}%
              </span>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-md p-6">
          <h3 className="font-semibold text-gray-900 mb-4">Credito y Velocity</h3>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Credito Disponible</span>
              <span className="text-sm font-bold text-emerald-600">{(credit?.credit_available || 0).toLocaleString()}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Credito Usado</span>
              <span className="text-sm font-bold">{(credit?.credit_used || 0).toLocaleString()}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Limite de Credito</span>
              <span className="text-sm font-bold">{(credit?.credit_limit || 0).toLocaleString()}</span>
            </div>
            <div className="border-t pt-2 mt-2"></div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Velocity 24h</span>
              <span className="text-sm font-bold">{(profile.velocity_24h || 0).toLocaleString()}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Velocity 7d</span>
              <span className="text-sm font-bold">{(profile.velocity_7d || 0).toLocaleString()}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Velocity 30d</span>
              <span className="text-sm font-bold">{(profile.velocity_30d || 0).toLocaleString()}</span>
            </div>
          </div>
        </div>
      </div>

      {/* CUALcoin Classification A/B/C */}
      {ccClassification && (
        <div className="bg-white rounded-xl shadow-md p-6" data-testid="cc-classification-section">
          <h3 className="font-semibold text-gray-900 mb-4">Clasificacion CUALcoin (Cc)</h3>
          <div className="grid grid-cols-3 gap-3">
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-center">
              <p className="text-xs font-medium text-emerald-600 mb-1">Tipo A</p>
              <p className="text-lg font-bold text-emerald-700" data-testid="cc-type-a">
                {ccClassification.classification?.type_a?.toFixed(4) || '0.0000'}
              </p>
              <p className="text-[10px] text-emerald-500 mt-1">Creditos de Servicio</p>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-center">
              <p className="text-xs font-medium text-blue-600 mb-1">Tipo B</p>
              <p className="text-lg font-bold text-blue-700" data-testid="cc-type-b">
                {ccClassification.classification?.type_b_consumed_events || 0}
              </p>
              <p className="text-[10px] text-blue-500 mt-1">Derechos Ejecutados</p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-center">
              <p className="text-xs font-medium text-amber-600 mb-1">Tipo C</p>
              <p className="text-lg font-bold text-amber-700" data-testid="cc-type-c">
                {ccClassification.classification?.type_c?.toFixed(4) || '0.0000'}
              </p>
              <p className="text-[10px] text-amber-500 mt-1">Valor Reservado</p>
            </div>
          </div>
        </div>
      )}

      {/* Support Seller Commissions - Only shown if user has earned any */}
      {commissions && commissions.total_count > 0 && (
        <div className="bg-white rounded-xl shadow-md p-6" data-testid="commissions-section">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-gray-900">Comisiones de Apoyo</h3>
            <div className="text-right">
              <p className="text-lg font-bold text-purple-600">{commissions.total_earned.toFixed(4)} CC</p>
              <p className="text-xs text-gray-500">{commissions.total_count} ventas</p>
            </div>
          </div>
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {commissions.commissions.map((c, i) => (
              <div key={i} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0" data-testid={`commission-${i}`}>
                <div>
                  <p className="text-sm text-gray-800">{c.listing_title}</p>
                  <p className="text-xs text-gray-400">{c.date ? new Date(c.date).toLocaleDateString('es-ES') : ''}</p>
                </div>
                <span className="text-sm font-medium text-purple-600">+{c.amount.toFixed(4)} {c.currency}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Preferencias de CUALia (capa de conciencia interna) */}
      <CualiaPreferences />
    </div>
  );
};




export default EconomyProfileComponent;
